import { GlobalStateController } from "bai-react-global-state";
import type { User } from "firebase/auth";
import _ from "lodash";
import moment from "moment";
import React from "react";

import { debug as debugApi, workspaces as workspacesApi } from "apis/callables";
import {
    CreateWorkspaceResourceFolderRequest,
    CreateWorkspaceResourceRequest,
    DeleteWorkspaceResourceRequest,
    GetWorkspaceResourcesRequest,
    UpdateWorkspaceRequest,
    UpdateWorkspaceResourceFolderRequest,
    UpdateWorkspaceResourceFolderResourcesRequest,
    UpdateWorkspaceResourceFolderUsersRequest,
    UpdateWorkspaceResourceRequest,
    UpdateWorkspaceResourceUsersRequest
} from "apis/types";
import {
    AnalyticsRolesAndLicensesMap,
    BEAUTIFUL_WORKSPACE_ACTION,
    PusherEventType,
    TEAM_USER_LICENSE_STATUS
} from "common/constants";
import {
    ActionPermissionsObject,
    GET_DEFAULT_BASIC_PLAN,
    GET_DEFAULT_RESTRICTED_ACTION_PERMISSIONS,
    GET_DEFAULT_RESTRICTED_PLAN_PERMISSIONS,
    ISharedThemeStorageModel,
    IThemeStorageModel,
    IUserStorageModel,
    IWorkspace,
    IWorkspaceDocumentChangedEvent,
    IWorkspaceIntegrationConfig,
    IWorkspaceMetadata,
    IWorkspacePermission,
    IWorkspacePlan,
    IWorkspaceResource,
    IWorkspaceResourceAsset,
    IWorkspaceResourceFolder,
    IWorkspaceUser,
    IWorkspaceUserExtended,
    IWorkspaceUserGroup,
    IWorkspaceUserInvite,
    LegacyTeamMemberRole,
    PermissionActionType,
    WorkspaceIntegrationType,
    WorkspacePermissionObjectType,
    WorkspacePermissionSubjectType,
    WorkspacePlanTier,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
    WorkspaceUserRole
} from "common/interfaces";
import { getSequentialRunner, SequentialRunner } from "common/utils/runSequentially";
import {
    getActionPermissionsCombinedWithPlanPermissions,
    getCombinedActionPermissions
} from "common/utils/workspaces/permissions";
import { getLegacyRoleFromWorkspaceUserRole } from "common/utils/workspaces/users";
import Analytics from "js/analytics";
import BillingController from "js/controllers/BillingController";
import PresentationLibraryController from "js/controllers/PresentationLibraryController";
import AppController from "js/core/AppController";
import Api from "js/core/api";
import db from "js/db";
import getLogger, { LogGroup } from "js/core/logger";
import { SharedTheme } from "js/core/models/sharedTheme";
import { Theme } from "js/core/models/theme";
import pusher, { ExtendedChannel } from "js/core/services/pusher";
import { ClipboardType, clipboardWrite } from "js/core/utilities/clipboard";
import { download, trackActivity, trackState } from "js/core/utilities/utilities";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import MandatoryUpdateDialog from "js/react/components/Dialogs/MandatoryUpdateDialog";
import Spinner from "js/react/components/Spinner";
import { reactMount, reactUnmount } from "js/react/renderReactRoot";
import { getExperiments } from "js/core/services/experiments";
import EnterpriseWorkspaceBlockedDialog from "js/react/views/Billing/dialogs/EnterpriseWorkspaceBlockedDialog";
import PaymentIssueDialog from "js/react/views/Billing/dialogs/PaymentIssueDialog";
import { ConflictError } from "apis/errors";

const logger = getLogger(LogGroup.WORKSPACES);

export interface WorkspaceControllerState {
    initialized: boolean;
    initializeError: Error | null;
    workspace: IWorkspace;
    users: IWorkspaceUserExtended[];
    userInvites: IWorkspaceUserInvite[];
    userGroups: IWorkspaceUserGroup[];
    resourceFolders: IWorkspaceResourceFolder[];
    resources: IWorkspaceResource[];
    isPastDue: boolean;
    plan: IWorkspacePlan;
    experiments: {
        [experimentId: string]: boolean;
    }
}

const initialState: WorkspaceControllerState = {
    initialized: false,
    initializeError: null,
    workspace: null,
    users: [],
    userInvites: [],
    userGroups: [],
    resourceFolders: [],
    resources: [],
    isPastDue: false,
    plan: GET_DEFAULT_BASIC_PLAN(),
    experiments: {}
};

class WorkspaceController extends GlobalStateController<WorkspaceControllerState> {
    private _pusherChannel: ExtendedChannel;
    private _firebaseUser: User;
    private _userModel: IUserStorageModel;
    private _runSequentially: SequentialRunner;
    private _stateDidUpdateCallbacks: ((prevState: WorkspaceControllerState, currentState: WorkspaceControllerState) => void)[] = [];
    private _resourcePermissionsCache: { [resourceIdAndType: string]: Promise<IWorkspacePermission[]> } = {};
    private _sharedThemesCache: { [themeId: string]: Promise<ISharedThemeStorageModel> } = {};
    private _userThemesCache: { [themeId: string]: Promise<IThemeStorageModel> } = {};

    constructor() {
        super(_.cloneDeep(initialState));
        this._runSequentially = getSequentialRunner();
    }

    public get workspaceId() {
        return this._state.workspace?.id;
    }

    public get workspaceUser() {
        return this._state.users.find(user => user.uid === this._firebaseUser.uid);
    }

    public get workspaceUsers() {
        return this._state.users;
    }

    public get workspaceUserGroups() {
        return this._state.userGroups.filter(group => this.workspaceUser?.groupIds.includes(group.id));
    }

    public get role() {
        if (!this.initialized) {
            return WorkspaceUserRole.GUEST;
        }

        return this.workspaceUser.role;
    }

    public get usedSeatCount() {
        return this._state.users.filter(user => user.role !== WorkspaceUserRole.GUEST).length + this._state.userInvites.filter(invite => invite.role !== WorkspaceUserRole.GUEST).length;
    }

    public get legacyRole() {
        if (!this.initialized) {
            return LegacyTeamMemberRole.MEMBER;
        }

        return getLegacyRoleFromWorkspaceUserRole(this.workspaceUser.role);
    }

    public get isOwner() {
        if (!this.initialized) {
            return false;
        }

        return this.workspaceUser.role === WorkspaceUserRole.OWNER;
    }

    public get isGuest() {
        if (!this.initialized) {
            return true;
        }
        return this.role === WorkspaceUserRole.GUEST;
    }

    public get isBasic() {
        if (!this.initialized) {
            return false;
        }
        return this.plan.tier === WorkspacePlanTier.BASIC;
    }

    public get isBasicOrGuest() {
        if (!this.initialized) {
            return true;
        }
        return this.isGuest || this.isBasic;
    }

    public get actionPermissions() {
        if (!this.initialized) {
            return GET_DEFAULT_RESTRICTED_ACTION_PERMISSIONS();
        }

        return getActionPermissionsCombinedWithPlanPermissions(
            getCombinedActionPermissions((this.workspaceUser?.groupIds.map(id => this._state.userGroups.find(group => group.id === id)?.actionPermissions) ?? []).filter(Boolean)),
            this.planPermissions
        );
    }

    public get planPermissions() {
        if (!this.initialized) {
            return GET_DEFAULT_RESTRICTED_PLAN_PERMISSIONS();
        }

        return this._state.plan.permissions;
    }

    public get isPastDue() {
        return this._state.isPastDue;
    }

    public get isEnterprise() {
        return this._state.plan.tier === WorkspacePlanTier.ENTERPRISE;
    }

    public get initialized() {
        return this._state.initialized;
    }

    public get plan() {
        return this._state.plan;
    }

    public get resourceFolders() {
        return this._state.resourceFolders;
    }

    public get resources() {
        return this._state.resources;
    }

    public get userInvites() {
        return this._state.userInvites;
    }

    public get userGroups() {
        return this._state.userGroups;
    }

    public get users() {
        return this._state.users;
    }

    public get workspace() {
        return this._state.workspace;
    }

    public get existingTags() {
        return [
            ...new Set(this.resources.map(resource => resource.tags).flat())
        ];
    }

    public get experiments() {
        return this._state.experiments;
    }

    /**
     * Helper to check if the workspace is a multi-user workspace (i.e. team or enterprise)
     */
    public get isMultiUserWorkspace() {
        return [WorkspacePlanTier.TEAM, WorkspacePlanTier.ENTERPRISE].includes(this.plan.tier);
    }

    private _initialize(workspaceId: string, firebaseUser: User, userModel: IUserStorageModel) {
        return this._runSequentially(async () => {
            await this.reset(false);

            logger.info("[WorkspaceController] initialize()", { workspaceId, uid: firebaseUser.uid });

            try {
                const [workspace, users, userGroups, resourceFolders, resources, plan, userInvites] = await Promise.all([
                    workspacesApi.getWorkspace({ workspaceId }),
                    workspacesApi.getWorkspaceUsers({ workspaceId }),
                    workspacesApi.getWorkspaceUserGroups({ workspaceId }),
                    workspacesApi.getWorkspaceResourceFolders({ workspaceId }),
                    workspacesApi.getWorkspaceResources({ workspaceId }),
                    workspacesApi.getWorkspacePlan({ workspaceId }),
                    workspacesApi.getWorkspaceUserInvites({ workspaceId })
                ]);

                this._pusherChannel = await pusher.subscribe(`private-workspace-${workspaceId}`);
                this._pusherChannel.bind(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);

                this._firebaseUser = firebaseUser;
                this._userModel = userModel;

                const { ai_gen_images } = await getExperiments(["ai_gen_images"]);

                await this._updateState({
                    workspace,
                    initialized: true,
                    initializeError: null,
                    users,
                    userGroups,
                    resourceFolders,
                    resources,
                    plan,
                    userInvites,
                    experiments: {
                        ai_gen_images: ai_gen_images.enabled
                    }
                });

                this._postInitialize();
            } catch (err) {
                logger.error(err, "[WorkspaceController] initialize() failed to initialize");

                await this._updateState({
                    initialized: false,
                    initializeError: err
                });
            }
        });
    }

    private _postInitialize() {
        // Show dialog if the user's role has changed while offline
        // TODO: this should be handled via a notification
        if (this._userModel.has("workspaceRoleChange")) {
            const roleChangedForWorkspaceId = this._userModel.get("workspaceRoleChange");
            if (roleChangedForWorkspaceId === this.workspaceId) {
                this._onWorkspaceRoleChanged();
            }
        }

        Analytics.util.trackState(
            _getAnalyticsProps(AppController.workspaceMetadata)
        );

        Analytics.util.identify(this._firebaseUser);

        this.checkPastDue();
    }

    private _dialogContainer: HTMLElement | null = null;

    private _removeDialogContainer() {
        if (this._dialogContainer) {
            reactUnmount(this._dialogContainer);
            this._dialogContainer.remove();
            this._dialogContainer = null;
        }
    }

    private _getDialogContainer() {
        this._removeDialogContainer();
        this._dialogContainer = document.createElement("div");
        this._dialogContainer.id = "workspace-billing-dialog-container";
        document.body.append(this._dialogContainer);
        return this._dialogContainer;
    }

    private _reset(runSequentially = true) {
        const reset = async () => {
            logger.info("[WorkspaceController] reset()");

            if (this._pusherChannel) {
                this._pusherChannel.unbind(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);
                if (!this._pusherChannel.isInUse) {
                    pusher.unsubscribe(this._pusherChannel.name);
                }
                this._pusherChannel = null;
            }

            await this._updateState(_.cloneDeep(initialState));

            await BillingController.reset();
        };

        if (runSequentially) {
            return this._runSequentially(reset);
        }

        return reset();
    }

    protected _stateDidUpdate(prevState: WorkspaceControllerState) {
        const { initialized, users, workspace, userGroups, plan } = this._state;

        try {
            this._stateDidUpdateCallbacks.forEach(callback => callback(prevState, this._state));
        } catch (err) {
            logger.error(err, "[WorkspaceController] _stateDidUpdate() failed to run stateDidUpdate callbacks");
        }

        if (!initialized) {
            return;
        }

        const handleUsersUpdate = () => {
            if (_.isEqual(prevState.users, users)) {
                return;
            }

            const uid = this._firebaseUser.uid;

            const currentUserBefore = prevState.users.find(user => user.uid === uid);
            const currentUserAfter = this.workspaceUser;

            if (!currentUserBefore || !currentUserAfter) {
                return;
            }

            const roleBefore = getLegacyRoleFromWorkspaceUserRole(currentUserBefore.role);
            const roleAfter = this.legacyRole;
            if (roleBefore !== roleAfter) {
                this._onWorkspaceRoleChanged();
            }
        };

        const handleWorkspacesMetadataUpdate = () => {
            if (prevState.workspace?.name !== workspace.name || !_.isEqual(prevState.plan, plan)) {
                this._userModel.loadWorkspacesMetadata([this.workspaceId])
                    .catch(err => {
                        logger.error(err, "[WorkspaceController] handleWorkspacesMetadataUpdate() failed to reload user workspaces metadata");
                    });
            }
        };

        const handleActionPermissionsUpdate = () => {
            if (_.isEqual(prevState.users, users) && _.isEqual(prevState.userGroups, userGroups)) {
                return;
            }

            const uid = this._firebaseUser.uid;
            const currentUserBefore = prevState.users.find(user => user.uid === uid);
            const actionPermissionsBefore = getCombinedActionPermissions(
                (currentUserBefore?.groupIds.map(id => prevState.userGroups.find(group => group.id === id)?.actionPermissions) ?? []).filter(Boolean)
            );

            // If the user has billing access, we need the billing controller to be initialized, otherwise we need to reset it
            if (actionPermissionsBefore[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE] !== this.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE]) {
                if (!this.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE]) {
                    BillingController.reset();
                    return;
                }

                if (BillingController.initialized) {
                    return;
                }

                this._initializeBillingController();
            }
        };

        handleUsersUpdate();
        handleWorkspacesMetadataUpdate();
        handleActionPermissionsUpdate();
    }

    private _initializeBillingController() {
        return BillingController.initialize(this.workspaceId, this._firebaseUser, this._userModel)
            .catch(err => {
                logger.error(err, "[WorkspaceController] _initializeBillingController() failed to initialize BillingController");
            });
    }

    private _onPusherEvent = (event: IWorkspaceDocumentChangedEvent) => {
        this._runSequentially(async () => {
            if (event.documentType === "Workspace") {
                const workspace = event.newDocument as IWorkspace;
                if (workspace.modifiedAt > this._state.workspace.modifiedAt) {
                    this._updateState({ workspace });
                }
            }

            if (event.documentType === "WorkspaceUser") {
                if (event.operationType === "create" || event.operationType === "update") {
                    const workspaceUser = event.newDocument as IWorkspaceUserExtended;

                    const userBeforeUpdate = this._state.users.find(user => user.uid === workspaceUser.uid);
                    if ((userBeforeUpdate?.modifiedAt ?? 0) < workspaceUser.modifiedAt) {
                        this._updateState(state => ({
                            ...state,
                            users: _.cloneDeep([...state.users.filter(user => user.uid !== workspaceUser.uid), workspaceUser])
                        }));
                    }
                } else if (event.operationType === "delete") {
                    const workspaceUser = event.oldDocument as IWorkspaceUserExtended;

                    const userBeforeDelete = this._state.users.find(user => user.uid === workspaceUser.uid);
                    if ((userBeforeDelete?.modifiedAt ?? 0) < event.timestamp) {
                        this._updateState(state => ({
                            ...state,
                            users: _.cloneDeep(state.users.filter(user => user.uid !== workspaceUser.uid))
                        }));
                    }
                }
            }

            if (event.documentType === "WorkspaceUserGroup") {
                const workspaceUserGroup = event.newDocument as IWorkspaceUserGroup;
                const groupId = event.documentId;

                if (event.operationType === "create" || event.operationType === "update") {
                    const groupBeforeUpdate = this._state.userGroups.find(group => group.id === groupId);
                    if ((groupBeforeUpdate?.modifiedAt ?? 0) < workspaceUserGroup.modifiedAt) {
                        this._updateState(state => ({
                            ...state,
                            userGroups: _.cloneDeep([...state.userGroups.filter(group => group.id !== groupId), workspaceUserGroup])
                        }));
                    }
                } else if (event.operationType === "delete") {
                    const groupBeforeDelete = this._state.userGroups.find(group => group.id === groupId);
                    if ((groupBeforeDelete?.modifiedAt ?? 0) < event.timestamp) {
                        this._updateState(state => ({
                            ...state,
                            userGroups: _.cloneDeep(state.userGroups.filter(group => group.id !== groupId))
                        }));
                    }
                }
            }

            if (event.documentType === "WorkspaceResourceFolder") {
                const workspaceResourceFolder = event.newDocument as IWorkspaceResourceFolder;
                const folderId = event.documentId;

                const permissions = await workspacesApi.getUserPermissionsToResourceFolder({
                    workspaceId: this.workspaceId,
                    folderId
                });

                if (event.operationType === "create" || event.operationType === "update") {
                    if (!permissions.read.object) {
                        // Don't have permissions, so was reported for a different user, ignore
                        return;
                    }

                    const folderBeforeUpdate = this._state.resourceFolders.find(folder => folder.id === folderId);
                    if ((folderBeforeUpdate?.modifiedAt ?? 0) < workspaceResourceFolder.modifiedAt) {
                        this._updateState(state => ({
                            ...state,
                            resourceFolders: _.cloneDeep([...state.resourceFolders.filter(folder => folder.id !== folderId), workspaceResourceFolder])
                        }));
                    }
                } else if (event.operationType === "delete") {
                    if (permissions.read.object) {
                        // Still have permissions, so was reported for a different user, ignore
                        return;
                    }

                    const folderBeforeDelete = this._state.resourceFolders.find(folder => folder.id === folderId);
                    if ((folderBeforeDelete?.modifiedAt ?? 0) < event.timestamp) {
                        this._updateState(state => ({
                            ...state,
                            resourceFolders: _.cloneDeep(state.resourceFolders.filter(folder => folder.id !== folderId))
                        }));
                    }
                }
            }

            if (event.documentType === "WorkspacePlan") {
                const plan = event.newDocument as IWorkspacePlan;
                this._updateState({ plan });
            }

            if (event.documentType === "WorkspaceUserInvite") {
                const inviteId = event.documentId;

                if (event.operationType === "create" || event.operationType === "update") {
                    const invite = event.newDocument as IWorkspaceUserInvite;
                    const inviteBeforeUpdate = this._state.userInvites.find(i => invite.id === inviteId);
                    if ((inviteBeforeUpdate?.modifiedAt ?? 0) < invite.modifiedAt) {
                        if (invite.acceptedAt) {
                            this._updateState(state => ({
                                ...state,
                                userInvites: _.cloneDeep(state.userInvites.filter(invite => invite.id !== inviteId))
                            }));
                        } else {
                            this._updateState(state => ({
                                ...state,
                                userInvites: _.cloneDeep([...state.userInvites.filter(invite => invite.id !== inviteId), invite])
                            }));
                        }
                    }
                } else if (event.operationType === "delete") {
                    const inviteBeforeDelete = this._state.userInvites.find(invite => invite.id === inviteId);
                    if ((inviteBeforeDelete?.modifiedAt ?? 0) < event.timestamp) {
                        this._updateState(state => ({
                            ...state,
                            userInvites: _.cloneDeep(state.userInvites.filter(invite => invite.id !== inviteId))
                        }));
                    }
                }
            }

            if (event.documentType === "WorkspaceResource") {
                const workspaceResource = event.operationType === "delete" ? event.oldDocument : event.newDocument;

                const resourceType = workspaceResource.resourceType;
                const resourceId = workspaceResource.resourceId;

                const permissions = await workspacesApi.getUserPermissionsToResource({
                    workspaceId: this.workspaceId,
                    resourceType,
                    resourceId
                });

                if (event.operationType === "create" || event.operationType === "update") {
                    if (!permissions.read.object) {
                        // Don't have permissions, so was reported for a different user, ignore
                        return;
                    }

                    const resourceBeforeUpdate = this._state.resources.find(resource => resource.resourceId === resourceId);
                    if ((resourceBeforeUpdate?.modifiedAt ?? 0) < workspaceResource.modifiedAt) {
                        this._updateState(state => ({
                            ...state,
                            resources: _.cloneDeep([...state.resources.filter(resource => resource.resourceId !== resourceId), workspaceResource])
                        }));
                    }
                } else if (event.operationType === "delete") {
                    if (permissions.read.object) {
                        // Still have permissions, so was reported for a different user, ignore
                        return;
                    }

                    const resourceBeforeDelete = this._state.resources.find(resource => resource.resourceId === resourceId && resource.resourceType === resourceType);
                    if ((resourceBeforeDelete?.modifiedAt ?? 0) < event.timestamp) {
                        this._updateState(state => ({
                            ...state,
                            resources: _.cloneDeep(state.resources.filter(resource => !(resource.resourceId === resourceId && resource.resourceType === resourceType)))
                        }));
                    }
                }
            }

            if (event.documentType === "WorkspacePermission") {
                if (event.operationType === "delete") {
                    const workspacePermission = event.oldDocument as IWorkspacePermission;
                    const objectId = workspacePermission.objectId;
                    const objectType = workspacePermission.objectType;

                    if (objectType === WorkspacePermissionObjectType.WORKSPACE_RESOURCE) {
                        const resource = this.resources.find(resource => resource.id === objectId);
                        if (resource) {
                            await this.forceRefreshWorkspaceResource(resource.resourceId, resource.resourceType, false);
                        }
                    }

                    if (objectType === WorkspacePermissionObjectType.RESOURCE_FOLDER) {
                        const folder = this.resourceFolders.find(folder => folder.id === objectId);
                        if (folder) {
                            await this.forceRefreshWorkspaceResourceFolders([folder.id], false);
                        }
                    }
                }
            }

            if (event.documentType === "WorkspaceUserInvite") {
                const inviteId = event.documentId;

                if (event.operationType === "create" || event.operationType === "update") {
                    const invite = event.newDocument;

                    const inviteBeforeUpdate = this._state.userInvites.find(invite => invite.id === inviteId);
                    if ((inviteBeforeUpdate?.modifiedAt ?? 0) < invite.modifiedAt) {
                        this._updateState(state => ({
                            ...state,
                            userInvites: _.cloneDeep([...state.userInvites.filter(invite => invite.id !== inviteId), invite].filter(invite => !invite.acceptedAt))
                        }));
                    }
                }
            }
        });
    }

    private async _onWorkspaceRoleChanged() {
        this._userModel.update({ workspaceRoleChange: null });
        await this._userModel.updatePromise.catch(err => {
            logger.error(err, "[WorkspaceController] _onWorkspaceRoleChanged() failed to clear workspaceRoleChange on user");
        });

        const role = this.legacyRole;
        const workspaceName = this._state.workspace.name;

        let message: string;
        switch (role) {
            case LegacyTeamMemberRole.OWNER:
                message = `Your role was changed to Owner in ${workspaceName}. Owners can manage members, Team Slides, and the Team Theme. Please refresh the browser to reflect the latest changes.`;
                break;
            case LegacyTeamMemberRole.LIBRARIAN:
                message = `Your role was changed to Librarian in ${workspaceName}. Librarians can manage Team Slides and the Team Theme. Please refresh the browser to reflect the latest changes.`;
                break;
            case LegacyTeamMemberRole.MEMBER_LICENSED:
                message = `Your role was changed to Pro Member in ${workspaceName}. Pro Members have access to Beautiful.ai Pro features. Please refresh the browser to reflect the latest changes.`;
                break;
            case LegacyTeamMemberRole.MEMBER:
                message = `Your role was changed to Free Team Member in ${workspaceName}. Free Team Members are limited to basic features. Please refresh the browser to reflect the latest changes.`;
                break;
        }

        ShowDialog(MandatoryUpdateDialog, { message });
    }

    /**
     * WARNING: Use only for resources which permissions are not supposed to change
     */
    private async _getWorkspacePermissionsToResourceCached(resourceId: string, resourceType: WorkspaceResourceType) {
        const key = `${resourceId}-${resourceType}`;
        if (!this._resourcePermissionsCache[key]) {
            this._resourcePermissionsCache[key] = workspacesApi.getWorkspacePermissionsToResource({ workspaceId: this.workspaceId, resourceId, resourceType });
        }
        return this._resourcePermissionsCache[key];
    }

    private async _getSharedThemeCached(themeId: string) {
        if (!this._sharedThemesCache[themeId]) {
            this._sharedThemesCache[themeId] = (async () => {
                const theme = new SharedTheme({ id: themeId }, { autoLoad: false });
                await theme.load();
                return theme;
            })();
        }
        return this._sharedThemesCache[themeId];
    }

    private async _getUserThemeCached(themeId: string) {
        if (!this._userThemesCache[themeId]) {
            this._userThemesCache[themeId] = (async () => {
                const theme = new Theme({ id: themeId }, { autoLoad: false });
                await theme.load();
                return theme;
            })();
        }
        return this._userThemesCache[themeId];
    }

    public initialize(workspaceId: string, firebaseUser: User, userModel: IUserStorageModel) {
        return this._initialize(workspaceId, firebaseUser, userModel);
    }

    public reset(runSequentially = true) {
        return this._reset(runSequentially);
    }

    /**
    * Wraps the given component with a state injector that renders the component only if the controller
    * is initialized.
    *
    * !! THIS WRAPPER WILL *NOT* INITIALIZE THE CONTROLLER IF IT'S NOT ALREADY INITIALIZED !!
    */
    public withInitializedState<T extends React.ComponentType<any>>(Component: T, PreloadComponent: React.ComponentType<any> = Spinner, ErrorComponent: React.ComponentType<any> = () => null) {
        function Wrapper(props: React.ComponentProps<T>) {
            const { initialized, initializeError } = props;

            if (initializeError) {
                return <ErrorComponent {...props} />;
            }

            if (!initialized) {
                return <PreloadComponent {...props} />;
            }

            return <Component {...props} />;
        }

        return this.withState(Wrapper);
    }

    /**
     * Registers a callback to be called after the state of the WorkspaceController changes.
     * @param callback - The callback to be called when the state changes.
     * @returns A function to remove the callback.
     */
    public onStateDidUpdate(callback: (prevState: WorkspaceControllerState, currentState: WorkspaceControllerState) => void) {
        this._stateDidUpdateCallbacks.push(callback);
        return () => {
            this._stateDidUpdateCallbacks = this._stateDidUpdateCallbacks.filter(cb => cb !== callback);
        };
    }

    public getUsersWithAccessToResourceFolder(folderId: string) {
        const { resourceFolders } = this._state;

        const folder = resourceFolders.find(f => f.id === folderId);
        if (!folder) {
            throw new Error("Folder not found");
        }

        return this.getUsersInGroup(folder.userGroupId);
    }

    public getUsersInGroup(groupId: string) {
        const { users, userGroups } = this._state;

        const groups = userGroups.filter(group => group.groupIds.includes(groupId) || group.id === groupId).map(group => group.id);

        const getMoreGroups = () => userGroups.filter(group => !groups.includes(group.id) && group.groupIds.some(g => groups.includes(g)));
        while (getMoreGroups().length > 0) {
            groups.push(...getMoreGroups().map(group => group.id));
        }

        return users.filter(user => user.groupIds.some(g => groups.includes(g)));
    }

    public async getUserThemeResources() {
        const { resources } = this._state;

        const themeResources = resources.filter(resource => resource.resourceType === WorkspaceResourceType.THEME);
        const resourcesWithPermissions = await Promise.all(themeResources.map(async resource => ({
            resource,
            permissions: await this._getWorkspacePermissionsToResourceCached(resource.resourceId, resource.resourceType)
        })));

        return resourcesWithPermissions.filter(({ permissions }) => permissions.length === 1 && permissions[0].subjectType === WorkspacePermissionSubjectType.USER).map(resource => resource.resource);
    }

    public async getUserThemes(readonly = false, skipPrivate = true) {
        const userThemeResources = await this.getUserThemeResources();
        let userThemes = await Promise.all(userThemeResources.map(resource => this._getUserThemeCached(resource.resourceId)));

        if (skipPrivate) {
            // "private" is a legacy flag set on some really old themes that aren't supposed to be displayed
            // in the UI
            userThemes = userThemes.filter(theme => !theme.get("private"));
        }

        if (!readonly) {
            return userThemes;
        }

        return userThemes.map(theme => new Theme(_.cloneDeep(theme.attributes), { disconnected: true, autoLoad: false }));
    }

    public async getUserTheme(themeId: string, readonly = false) {
        const resource = this.resources.find(resource => resource.resourceType === WorkspaceResourceType.THEME && resource.resourceId === themeId);
        if (!resource) {
            return null;
        }

        const permissions = await this._getWorkspacePermissionsToResourceCached(themeId, WorkspaceResourceType.THEME);
        if (permissions.length !== 1 || permissions[0].subjectType !== WorkspacePermissionSubjectType.USER) {
            return null;
        }

        const theme = await this._getUserThemeCached(themeId);

        if (!readonly) {
            return theme;
        }

        return new Theme(_.cloneDeep(theme.attributes), { disconnected: true, autoLoad: false });
    }

    public async getSharedThemeResources() {
        const { resources } = this._state;

        const themeResources = resources.filter(resource => resource.resourceType === WorkspaceResourceType.THEME);
        const resourcesWithPermissions = await Promise.all(themeResources.map(async resource => ({
            resource,
            permissions: await this._getWorkspacePermissionsToResourceCached(resource.resourceId, resource.resourceType)
        })));

        return resourcesWithPermissions.filter(({ permissions }) => !(permissions.length === 1 && permissions[0].subjectType === WorkspacePermissionSubjectType.USER)).map(resource => resource.resource);
    }

    public async getSharedThemes(readonly = false, skipHidden = false) {
        const { resources } = this._state;
        const defaultThemeId = resources.find(resource => resource.resourceType === WorkspaceResourceType.THEME && resource.metadata.isDefault)?.resourceId;

        const sharedThemeResources = await this.getSharedThemeResources();
        let sharedThemes = await Promise.all(sharedThemeResources.map(resource => this._getSharedThemeCached(resource.resourceId)));

        // First find the default theme
        const defaultTheme = sharedThemes.find(theme => theme.id === defaultThemeId);
        // Then get the other themes
        const otherThemes = sharedThemes.filter(theme => theme.id !== defaultThemeId);
        // Then sort the themes with the default theme first if it exists
        sharedThemes = defaultTheme ? [defaultTheme, ...otherThemes] : otherThemes;

        if (skipHidden) {
            sharedThemes = sharedThemes.filter(theme => !theme.get("hidden"));
        }

        if (!readonly) {
            return sharedThemes;
        }

        return sharedThemes.map(theme => new SharedTheme(_.cloneDeep(theme.attributes), { disconnected: true, autoLoad: false }));
    }

    public async getSharedTheme(themeId: string, readonly = false) {
        const resource = this.resources.find(resource => resource.resourceType === WorkspaceResourceType.THEME && resource.resourceId === themeId);
        if (!resource) {
            return null;
        }

        const permissions = await this._getWorkspacePermissionsToResourceCached(themeId, WorkspaceResourceType.THEME);
        if (permissions.length === 1 && permissions[0].subjectType === WorkspacePermissionSubjectType.USER) {
            return null;
        }

        const sharedTheme = await this._getSharedThemeCached(themeId);

        if (!readonly) {
            return sharedTheme;
        }

        return new SharedTheme(_.cloneDeep(sharedTheme.attributes), { disconnected: true, autoLoad: false });
    }

    /**
     * Checks if the workspace is past due and shows the appropriate dialog.
     * @returns true if a dialog was shown, false otherwise.
     */
    public async checkPastDue() {
        const { users, userInvites } = this._state;

        try {
            const [metadata] = await getWorkspacesMetadata({ ids: [this.workspaceId] });
            if (!metadata) {
                return false;
            }

            // added default state for subscriptionState because in dev envs it can be undefined
            // because stripe removes the subscription after a while
            const { plan, forceLockWorkspace, accountManagerEmail, subscriptionCanceledAt, subscriptionChargeRequiresAction, subscriptionState } = metadata;

            // There are cases when the workspace subscription state is not defined
            const subscriptionStateType = subscriptionState?.type ?? null;

            // Enterprise
            if (plan.tier === WorkspacePlanTier.ENTERPRISE) {
                if (forceLockWorkspace || subscriptionStateType === "past_due" || subscriptionStateType === "terminal") {
                    reactMount(
                        <EnterpriseWorkspaceBlockedDialog
                            workspaceId={this.workspaceId}
                            isPastDue={subscriptionStateType === "past_due"}
                            forceLockWorkspace={forceLockWorkspace}
                            contactEmail={accountManagerEmail}
                            isCanceled={subscriptionStateType === "terminal"}
                            onClose={() => this._removeDialogContainer()}
                        />,
                        this._getDialogContainer(),
                        false
                    );
                    return true;
                }

                return false;
            }

            const isMultiUser = (
                users.filter(user => user.role !== WorkspaceUserRole.GUEST).length +
                userInvites.filter(invite => invite.role !== WorkspaceUserRole.GUEST).length
            ) > 1;

            // Terminal state
            if (isMultiUser && (subscriptionStateType === "terminal" || !subscriptionStateType)) {
                // @ts-ignore
                const { WorkspaceSubscriptionMissingOrCanceledDialog } = await import("js/react/views/Billing/dialogs/WorkspaceSubscriptionMissingOrCanceledDialog");
                reactMount(
                    <WorkspaceSubscriptionMissingOrCanceledDialog
                        subscriptionCanceledAt={subscriptionCanceledAt}
                        contactEmail={accountManagerEmail}
                        onClose={() => this._removeDialogContainer()}
                    />,
                    this._getDialogContainer(),
                    false
                );
                return true;
            }

            // Handle past-due payments for pro/non-enterprise workspaces
            if (this.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE] && (subscriptionChargeRequiresAction || subscriptionStateType === "past_due")) {
                reactMount(
                    <PaymentIssueDialog
                        onClose={() => this._removeDialogContainer()}
                    />,
                    this._getDialogContainer(),
                    false
                );
                return true;
            }

            return false;
        } catch (err) {
            logger.error(err, "[WorkspaceController] checkPastDue() failed");
            return false;
        }
    }

    /**
     * Determines if user's account can be upgraded (either their role or the workspace plan) to remove a restriction on a specific action.
     *
     * Used for determining if an upgrade CTA should be shown. The logic is adjusted to be as close as possible
     * to the legacy logic for determining if an upgrade CTA should be shown.
     *
     * Examples:
     * - User is guest and the action is not allowed for them, but the workspace plan allows the action, meaning upgrading to a paid member will remove the restriction (true is returned)
     * - User is not guest, and the action is restricted by the plan, meaning the restriction can be lifted by upgrading the workspace plan (true is returned)
     */
    public canUpgradeToRemoveActionRestriction(actionObject: ActionPermissionsObject, actionType: PermissionActionType) {
        if (this.actionPermissions[actionObject][actionType]) {
            // Already unrestricted
            return false;
        }

        if (this.role === WorkspaceUserRole.GUEST && this.planPermissions[actionObject]) {
            // Guest, means access is restricted by them being a guest,
            // so they can be upgraded to workspace member
            return true;
        }

        if (!this.planPermissions[actionObject]) {
            // User is not guest, but the action is restricted by the plan
            // so they can be upgraded
            return true;
        }

        return false;
    }

    public async forceRefreshWorkspace() {
        return this._runSequentially(async () => {
            const [workspace, plan] = await Promise.all([
                workspacesApi.getWorkspace({ workspaceId: this.workspaceId }),
                workspacesApi.getWorkspacePlan({ workspaceId: this.workspaceId })
            ]);
            await this._updateState({ workspace, plan });
        });
    }

    public async forceRefreshWorkspaceResourceFolders(folderIds: string[], runSequentially = true) {
        const refresh = async () => {
            const workspaceResourceFolders = await workspacesApi.getWorkspaceResourceFolders({ workspaceId: this.workspaceId, folderIds });
            await this._updateState(state => ({
                ...state,
                resourceFolders: _.cloneDeep([...state.resourceFolders.filter(folder => !folderIds.includes(folder.id)), ...workspaceResourceFolders])
            }));
        };

        if (runSequentially) {
            return this._runSequentially(refresh);
        }

        return refresh();
    }

    public async forceRefreshWorkspaceResources(resources: Pick<IWorkspaceResource, "resourceId" | "resourceType">[], runSequentially = true) {
        const refresh = async () => {
            const fetchedResources = await workspacesApi.getWorkspaceResources({ workspaceId: this.workspaceId, resources });

            await this._updateState(state => ({
                ...state,
                resources: _.cloneDeep([...state.resources.filter(r => !resources.some(rr => rr.resourceId === r.resourceId && rr.resourceType === r.resourceType)), ...fetchedResources])
            }));
        };

        if (runSequentially) {
            return this._runSequentially(refresh);
        }

        return refresh();
    }

    public async forceRefreshWorkspaceResource(resourceId: string, resourceType: WorkspaceResourceType, runSequentially = true) {
        return this.forceRefreshWorkspaceResources([{ resourceId, resourceType }], runSequentially);
    }

    public async updateWorkspace(update: UpdateWorkspaceRequest["update"]) {
        return this._runSequentially(async () => {
            const workspace = await workspacesApi.updateWorkspace({ workspaceId: this.workspaceId, update });

            //// Legacy analytics ////
            if (update.name) {
                const props = {
                    old_workspace_name: workspace.name,
                    new_workspace_name: update.name
                };
                trackState({ "workspace_name": update.name });
                trackActivity("Organization", "NameChanged", null, null, props, { audit: true });
            }
            ////

            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async updateWorkspaceUserGroups({ update }: {
        update: {
            [groupId: string]: {
                actionPermissions: Partial<IWorkspaceUserGroup["actionPermissions"]>;
            };
        }
    }) {
        return this._runSequentially(async () => {
            const userGroups = await workspacesApi.updateWorkspaceUserGroups({ workspaceId: this.workspaceId, update });
            await this._updateState(state => ({
                ...state,
                userGroups: _.cloneDeep([...state.userGroups.filter(group => !userGroups.some(ug => ug.id === group.id)), ...userGroups])
            }));
            return userGroups;
        });
    }

    public async updateWorkspaceResourceFolder(request: Omit<UpdateWorkspaceResourceFolderRequest, "workspaceId">) {
        return this._runSequentially(async () => {
            const workspaceResourceFolder = await workspacesApi.updateWorkspaceResourceFolder({ ...request, workspaceId: this.workspaceId });
            await this._updateState(state => ({
                ...state,
                resourceFolders: _.cloneDeep([...state.resourceFolders.filter(folder => folder.id !== request.folderId), workspaceResourceFolder])
            }));
            return workspaceResourceFolder;
        });
    }

    public async deleteWorkspaceResourceFolder({ folderId }: { folderId: string }) {
        return this._runSequentially(async () => {
            await workspacesApi.deleteWorkspaceResourceFolder({ workspaceId: this.workspaceId, folderId });
            await this._updateState(state => ({
                ...state,
                resourceFolders: _.cloneDeep(state.resourceFolders.filter(folder => folder.id !== folderId))
            }));
        });
    }

    public async createWorkspaceResourceFolder(request: Omit<CreateWorkspaceResourceFolderRequest, "workspaceId">) {
        return this._runSequentially(async () => {
            const { folder, userGroups, users } = await workspacesApi.createWorkspaceResourceFolder({ workspaceId: this.workspaceId, ...request });
            await this._updateState(state => ({
                ...state,
                resourceFolders: _.cloneDeep([...state.resourceFolders, folder]),
                userGroups: _.cloneDeep([...state.userGroups.filter(group => !userGroups.some(ug => ug.id === group.id)), ...userGroups]),
                users: _.cloneDeep([...state.users.filter(user => !users.some(u => u.uid === user.uid)), ...users])
            }));

            //// Legacy analytics ////
            if (request.folderType === WorkspaceResourceFolderType.PERSONAL) {
                trackActivity("Library", "NewFolder", null, null, { folder_id: folder.id, folder_name: folder.name }, { audit: false });
            } else {
                const props = {
                    workspace_id: this.workspaceId,
                    folder_id: folder.id,
                    folder_name: folder.name
                };
                trackActivity("OrgTeam", "Created", null, null, props);
            }
            ////

            return folder;
        });
    }

    public async updateWorkspaceResourceFolderResources(request: Omit<UpdateWorkspaceResourceFolderResourcesRequest, "workspaceId">) {
        return this._runSequentially(async () => {
            const workspaceResources = await workspacesApi.updateWorkspaceResourceFolderResources({ workspaceId: this.workspaceId, ...request });
            await this._updateState(state => ({
                ...state,
                resources: _.cloneDeep([...state.resources.filter(resource => !workspaceResources.some(wr => wr.id === resource.id)), ...workspaceResources])
            }));

            //// Legacy analytics ////
            const add = request.update.add ?? [];
            if (add.length > 0) {
                trackActivity("Folder", "PresentationsAdded", null, null, { ids: add.filter(r => r.resourceType === WorkspaceResourceType.PRESENTATION).map(r => r.resourceId) }, { audit: true });
            }

            const remove = request.update.remove ?? [];
            for (const resource of remove.filter(r => r.resourceType === WorkspaceResourceType.PRESENTATION)) {
                trackActivity("Folder", "PresentationRemoved", null, null, { presentation_id: resource.resourceId }, { audit: true });
            }
            ////

            return workspaceResources;
        });
    }

    /** Remove workspace resources from a folder */
    public async removeResourcesFromFolder(
        folderId: string,
        resources: { resourceId: string; resourceType: WorkspaceResourceType }[],
    ) {
        if (!resources.length) {
            return;
        }
        return this.updateWorkspaceResourceFolderResources({
            folderId,
            update: {
                remove: resources
            },
        });
    }

    public async updateWorkspaceResource(updates: Omit<UpdateWorkspaceResourceRequest, "workspaceId">, analyticsProps?: { isNewAsset?: boolean, isReplacedAsset?: boolean }) {
        return this._runSequentially(async () => {
            const workspaceResource = await workspacesApi.updateWorkspaceResource({ workspaceId: this.workspaceId, ...updates } as UpdateWorkspaceResourceRequest);
            await this._updateState(state => ({
                ...state,
                resources: _.cloneDeep([
                    ...state.resources.filter(resource => !(resource.resourceId === workspaceResource.resourceId && resource.resourceType === workspaceResource.resourceType)),
                    workspaceResource
                ])
            }));

            //// Legacy analytics ////
            if (updates.resourceType === WorkspaceResourceType.ASSET) {
                const metadata = updates.metadata as IWorkspaceResourceAsset["metadata"];
                trackActivity("TeamAsset", "Updated", null, null, {
                    id: updates.resourceId,
                    workspace_id: this.workspaceId,
                    type: metadata?.type,
                });

                if (analyticsProps?.isReplacedAsset) {
                    trackActivity("TeamAsset", "Replaced", null, null, {
                        id: updates.resourceId,
                        workspace_id: this.workspaceId,
                        type: metadata?.type,
                        isNew: analyticsProps?.isNewAsset
                    });
                }
            }
            ////

            return workspaceResource;
        });
    }

    public async deleteWorkspaceResource(request: Omit<DeleteWorkspaceResourceRequest, "workspaceId">, analyticsProps?: { trackActivity: string }) {
        return this._runSequentially(async () => {
            await workspacesApi.deleteWorkspaceResource({ workspaceId: this.workspaceId, ...request });

            //// Legacy analytics ////
            if (request.resourceType === WorkspaceResourceType.SLIDE || request.resourceType === WorkspaceResourceType.SLIDE_TEMPLATE) {
                const props = {
                    slide_id: request.resourceId,
                };
                trackActivity(analyticsProps?.trackActivity ?? "SharedSlideLibrary", "SlideDeleted", null, null, props);
            }
            if (request.resourceType === WorkspaceResourceType.ASSET) {
                const metadata = this.resources.find(resource => resource.resourceId === request.resourceId)?.metadata as IWorkspaceResourceAsset["metadata"];
                trackActivity("TeamAsset", "Deleted", null, null, {
                    id: request.resourceId,
                    workspace_id: this.workspaceId,
                    type: metadata?.type
                });
            }
            ////

            await this._updateState(state => ({
                ...state,
                resources: _.cloneDeep(state.resources.filter(resource => !(resource.resourceId === request.resourceId && resource.resourceType === request.resourceType)))
            }));
        });
    }

    public async createWorkspaceResource(request: { resource: Omit<CreateWorkspaceResourceRequest["resource"], "workspaceId">, defaultPermissions: CreateWorkspaceResourceRequest["defaultPermissions"] }, analyticsProps?: { template_name?: string, isNewAsset?: boolean, assetType?: string }) {
        return this._runSequentially(async () => {
            const workspaceResource = await workspacesApi.createWorkspaceResource({ ...request, resource: { ...request.resource, workspaceId: this.workspaceId }, workspaceId: this.workspaceId });

            //// Legacy analytics ////
            const resource = request.resource;
            if (resource.resourceType === WorkspaceResourceType.SLIDE || resource.resourceType === WorkspaceResourceType.SLIDE_TEMPLATE) {
                const props = {
                    slide_id: resource.resourceId,
                    template_name: analyticsProps?.template_name,
                };
                trackActivity("SharedSlideLibrary", "SlideAdded", null, null, props);
            }
            if (resource.resourceType === WorkspaceResourceType.ASSET) {
                trackActivity("TeamAsset", "Added", null, null, {
                    id: resource.resourceId,
                    workspace_id: this.workspace.id,
                    type: analyticsProps?.assetType,
                    isNewAsset: analyticsProps?.isNewAsset,
                });
            }
            if (resource.resourceType === WorkspaceResourceType.PRESENTATION_TEMPLATE) {
                trackActivity("PresentationTemplates", "PresentationTemplateAdded", null, null, {
                    id: resource.resourceId
                });
            }
            ////

            await this._updateState(state => ({ ...state, resources: _.cloneDeep([...state.resources.filter(resource => resource.resourceId !== workspaceResource.resourceId), workspaceResource]) }));
            return workspaceResource;
        });
    }

    public async verifyDomain(domain: string) {
        return this._runSequentially(async () => {
            const { workspace, isDomainVerified, domainVerificationError } = await workspacesApi.verifyWorkspaceDomain({ workspaceId: this.workspaceId, domain });
            await this._updateState({ workspace });
            return { workspace, isDomainVerified, domainVerificationError };
        });
    }

    public async removeDomain(domain: string) {
        return this._runSequentially(async () => {
            const workspace = await workspacesApi.removeWorkspaceDomain({ workspaceId: this.workspaceId, domain });
            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async addDomain(domain: string) {
        return this._runSequentially(async () => {
            const workspace = await workspacesApi.addWorkspaceDomain({ workspaceId: this.workspaceId, domain });
            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async parseSamlMetadata({ metadataUrl }: { metadataUrl: string }) {
        return this._runSequentially(async () => {
            const workspace = await workspacesApi.parseSamlMetadata({ workspaceId: this.workspaceId, metadataUrl });
            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async disconnectWorkspaceIntegration({ integrationType }: { integrationType: WorkspaceIntegrationType }) {
        return this._runSequentially(async () => {
            const workspace = await workspacesApi.disconnectWorkspaceIntegration({ workspaceId: this.workspaceId, integrationType });
            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async connectWorkspaceIntegration({ integrationType, config }: { integrationType: WorkspaceIntegrationType, config: IWorkspaceIntegrationConfig }) {
        return this._runSequentially(async () => {
            const workspace = await workspacesApi.connectWorkspaceIntegration({ workspaceId: this.workspaceId, integrationType, config });
            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async downloadUsersAndInvitesList() {
        const { users, userInvites } = this._state;

        const lines = [
            "Member,Email,Role,Pending,License,Last Active",
            ...[
                ...users.map(user => ({ ...user, pending: false, displayName: user.displayName ?? user.email })),
                ...userInvites.map(invite => ({ ...invite, pending: true, lastActive: null, displayName: invite.email }))
            ].map(member => ([
                member.displayName,
                member.email,
                getLegacyRoleFromWorkspaceUserRole(member.role),
                member.pending ? "Pending Invite" : "",
                member.role === WorkspaceUserRole.GUEST ? "free" : "team pro",
                member.lastActive ? moment(member.lastActive).format("YYYY-MM-DD HH:mm") : ""
            ].join(",")))
        ];

        download(`data:text/plain;charset=utf-8,${encodeURIComponent(lines.join("\n"))}`, `members.csv`);
    }

    public async resendUserInvite({ inviteId }: { inviteId: string }) {
        return this._runSequentially(async () => {
            const invite = await workspacesApi.resendWorkspaceUserInvite({ workspaceId: this.workspaceId, inviteId });

            //// Legacy analytics ////
            trackActivity("Organization", "ResendTeamInvite", null, null, {
                workspace_id: this.workspaceId,
                license: invite.role === WorkspaceUserRole.GUEST ? "free" : "pro",
                recipient_email: invite.email,
                sender_email: this._firebaseUser.email,
            }, { audit: true });
            ////

            await this._updateState(state => ({
                ...state,
                userInvites: _.cloneDeep([...state.userInvites.filter(invite => invite.id !== inviteId), invite])
            }));
        });
    }

    public async copyUserInviteLinkToClipboard({ invite }: { invite: IWorkspaceUserInvite }) {
        return this._runSequentially(async () => {
            const url = await workspacesApi.getWorkspaceUserInviteUrl({
                workspaceId: invite.workspaceId,
                inviteId: invite.id
            });

            await clipboardWrite({
                [ClipboardType.TEXT]: url,
            });

            //// Legacy analytics ////
            const props = {
                workspace_id: invite.workspaceId,
                license: invite.role === WorkspaceUserRole.GUEST ? "free" : "pro",
                recipient_email: invite.email,
                sender_email: this._firebaseUser.email,
                teamInviteUrl: url,
            };
            trackActivity("Organization", "CopyTeamInviteLink", null, null, props, { audit: true });
            ////
        });
    }

    public async deleteUserInvite({ inviteId }: { inviteId: string }) {
        return this._runSequentially(async () => {
            const deletedInvite = await workspacesApi.deleteWorkspaceUserInvite({ workspaceId: this.workspaceId, inviteId });

            //// Legacy analytics ////
            trackActivity("Organization", "InviteRemoved", null, null, { email: deletedInvite.email, workspace_id: this.workspaceId }, { audit: true });
            ////

            await this._updateState(state => ({
                ...state,
                userInvites: _.cloneDeep(state.userInvites.filter(invite => invite.id !== inviteId))
            }));
        });
    }

    public async updateUserInvite({ inviteId, update }: { inviteId: string, update: Pick<IWorkspaceUserInvite, "role"> }) {
        return this._runSequentially(async () => {
            const invite = await workspacesApi.updateWorkspaceUserInvite({ workspaceId: this.workspaceId, inviteId, update });
            await this._updateState(state => ({
                ...state,
                userInvites: _.cloneDeep([...state.userInvites.filter(invite => invite.id !== inviteId), invite])
            }));
        });
    }

    public async inviteWorkspaceUsers(users: Pick<IWorkspaceUserInvite, "email" | "role">[], analyticsProps?: { [key: string]: any }) {
        return this._runSequentially(async () => {
            const userInvites = await workspacesApi.inviteWorkspaceUsers({ workspaceId: this.workspaceId, users });
            _trackInviteWorkspaceUsers(this.workspaceId, users, analyticsProps);
            Analytics.ModifyWorkspaceUsers.InviteUsersComplete({
                quantity_invites_sent: userInvites.length,
                quantity_new_seats_added: analyticsProps?.quantity_new_seats_added ?? 0,
                invite_ids: userInvites.map(invite => invite.id),
            });
            await this._updateState(state => ({ ...state, userInvites: _.cloneDeep([...state.userInvites.filter(invite => !userInvites.some(u => u.id === invite.id)), ...userInvites]) }));
        });
    }

    public async updateWorkspaceUser({ uid, update }: { uid: string, update: Pick<IWorkspaceUser, "role"> }) {
        return this._runSequentially(async () => {
            const user = await workspacesApi.updateWorkspaceUser({ workspaceId: this.workspaceId, uid, update });
            await this._updateState(state => ({ ...state, users: _.cloneDeep([...state.users.filter(user => user.uid !== uid), user]) }));
        });
    }

    public async deleteWorkspaceUser({ uid, transferOption, targetUid }: { uid: string, transferOption: "other" | "revoke" | "self", targetUid?: string }) {
        return this._runSequentially(async () => {
            const deletedUser = await deleteWorkspaceUser({ workspaceId: this.workspaceId, uid, transferOption, targetUid });

            await this._updateState(state => ({ ...state, users: _.cloneDeep(state.users.filter(user => user.uid !== uid)) }));

            return deletedUser;
        });
    }

    public async updateWorkspaceResourceUsers({ resourceId, resourceType, update }: Omit<UpdateWorkspaceResourceUsersRequest, "workspaceId">) {
        const { invites, resource, permissions, users } = await workspacesApi.updateWorkspaceResourceUsers({
            workspaceId: this.workspaceId,
            resourceId,
            resourceType,
            update
        });

        await this._updateState(state => ({
            ...state,
            resources: _.cloneDeep([...state.resources.filter(resource => resource.resourceId !== resourceId), resource]),
            users: _.cloneDeep([...state.users.filter(user => !users.some(u => u.uid === user.uid)), ...users])
        }));

        return { invites, resource, permissions, users };
    }

    public async updateWorkspaceResourceFolderUsers(request: Omit<UpdateWorkspaceResourceFolderUsersRequest, "workspaceId">) {
        const { resources } = this._state;

        const { userGroups, users, resourceFolder } = await workspacesApi.updateWorkspaceResourceFolderUsers({ workspaceId: this.workspaceId, ...request });

        await this._updateState(state => ({
            ...state,
            userGroups: _.cloneDeep([...state.userGroups.filter(group => !userGroups.some(g => g.id === group.id)), ...userGroups]),
            users: _.cloneDeep([...state.users.filter(user => !users.some(u => u.uid === user.uid)), ...users]),
            resourceFolders: _.cloneDeep([...state.resourceFolders.filter(folder => folder.id !== request.folderId), resourceFolder]).filter(Boolean)
        }));

        if (!resourceFolder) {
            const resourcesInFolder = resources.filter(resource => resource.folderIds.includes(request.folderId));
            await this.forceRefreshWorkspaceResources(resourcesInFolder);
        }

        //// Legacy analytics ////
        try {
            const workspaceId = this.workspaceId;
            const { folderId, update } = request;

            if (resourceFolder && ("add" in update || "remove" in update)) {
                if ("add" in update) {
                    const addedUids = update.add.filter(p => p.subjectType === WorkspacePermissionSubjectType.USER).map(p => p.subjectId);

                    const props = {
                        workspace_id: workspaceId,
                        workspace_name: this.workspace.name,
                        folder_id: folderId,
                        folder_name: resourceFolder.name,
                        userid_added: addedUids,
                        userid_removed: null,
                        num_team_members: users.length,
                        num_changed: addedUids.length,
                        change_mechanism: "admin initiated"
                    };
                    trackActivity("OrgTeam", "MembershipChanged", null, null, props, { audit: true });
                }

                if ("remove" in update) {
                    const removedUids = update.remove.filter(p => p.subjectType === WorkspacePermissionSubjectType.USER).map(p => p.subjectId);

                    const props = {
                        workspace_id: workspaceId,
                        workspace_name: this.workspace.name,
                        folder_id: folderId,
                        folder_name: resourceFolder.name,
                        userid_added: null,
                        userid_removed: removedUids,
                        num_team_members: users.length,
                        num_changed: removedUids.length,
                        change_mechanism: removedUids.includes(auth().currentUser.uid) ? "user initiated" : "admin initiated"
                    };
                    trackActivity("OrgTeam", "MembershipChanged", null, null, props, { audit: true });
                }
            }
        } catch (err) {
            logger.error(err, "[WorkspaceController] updateWorkspaceResourceFolderUsers() failed to track analytics");
        }
        ////
    }

    public async printWorkspaceDebugData() {
        try {
            const { url } = await debugApi.getWorkspaceDebugData({ workspaceId: this.workspaceId });

            if (!url) {
                logger.info("[WorkspaceController] printWorkspaceDebugData() no data");
                return;
            }

            window.open(`/admin/debug?url=${encodeURIComponent(url)}`, "_blank");
        } catch (err) {
            logger.error(err, "[WorkspaceController] printWorkspaceDebugData() failed");
        }
    }
}

const workspaceController = new WorkspaceController();

// for lazy import and debug
app.workspaceController = workspaceController;

export default workspaceController;

function _getAnalyticsProps(workspaceMetadata: IWorkspaceMetadata) {
    return {
        workspace_id: workspaceMetadata.id,
        workspace_type: "workspace",
        workspace_name: workspaceMetadata.name,
        workspace_plan: workspaceMetadata.plan.name,
        // Cheap, but inaccurate as PresentationLibraryController may be not initialized in which case
        // it will return zero
        current_slide_count: PresentationLibraryController.getSlideCount(workspaceMetadata.id),
        role: workspaceMetadata.userRole
    };
}

function _trackInviteWorkspaceUsers(workspaceId: string, users: Pick<IWorkspaceUserInvite, "role" | "email">[], analyticsProps?: { [key: string]: any }) {
    const inviteProps = {
        workspace_id: workspaceId,
        recipients: users.map(u => u.email),
        user_roles: users.map(u => {
            if (u.role === WorkspaceUserRole.GUEST) {
                return AnalyticsRolesAndLicensesMap["free"];
            }
            if (u.role === WorkspaceUserRole.LIBRARIAN) {
                return AnalyticsRolesAndLicensesMap["librarian"];
            }
            if (u.role === WorkspaceUserRole.MEMBER) {
                return AnalyticsRolesAndLicensesMap["team_pro"];
            }
            return AnalyticsRolesAndLicensesMap["owner"];
        }),
        user_license: users.map(u => {
            if (u.role === WorkspaceUserRole.GUEST) {
                return TEAM_USER_LICENSE_STATUS.FREE;
            }
            return TEAM_USER_LICENSE_STATUS.TEAM_PRO;
        }),
        ...(analyticsProps ?? {})
    };
    trackActivity("Organization", "InviteSent", null, null, inviteProps, { audit: true });
}

/**
 * Returns legacyanalytics props for workspace
 *
 * Stateless if the controller is not initialized
 *
 * @param workspaceId The id of the workspace to get props for
 * @param uid The id of the user to get props for
 * @returns The analytics props for the workspace
 */
export async function getAnalyticsPropsForWorkspace(workspaceId: string) {
    const uid = auth().currentUser?.uid;
    if (!uid) {
        return {};
    }

    const metadata = await workspacesApi.getWorkspacesMetadata({ ids: [workspaceId] });
    if (metadata.length === 0) {
        return {};
    }

    const workspaceMetadata = metadata[0];
    return _getAnalyticsProps(workspaceMetadata);
}

/**
 * Returns the legacy role for a user in a workspace
 *
 * Stateless if the controller is not initialized
 *
 * @param workspaceId The id of the workspace to get the legacy role for
 * @returns The legacy role for the user in the workspace
 */
export async function getLegacyRoleInWorkspace(workspaceId: string) {
    const uid = auth().currentUser?.uid;
    if (!uid) {
        return null;
    }

    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return getLegacyRoleFromWorkspaceUserRole(workspaceController.workspaceUsers.find(user => user.uid === uid)?.role);
    }

    try {
        const [user] = await workspacesApi.getWorkspaceUsers({ workspaceId, uids: [uid] });
        return getLegacyRoleFromWorkspaceUserRole(user.role);
    } catch (err) {
        logger.error(err, "[WorkspaceController] getLegacyRoleInWorkspace() failed");
        return null;
    }
}

/**
 * Returns the initial workspace ID to show when the app loads
 *
 * Stateless if the controller is not initialized
 *
 * @param workspacesMetadata The list of workspaces metadata
 * @param recentWorkspaceId The most recently selected workspace id from the user record
 * @returns The initial workspace ID to show
 */
export function getInitialWorkspaceId(workspacesMetadata: IWorkspaceMetadata[], recentWorkspaceId?: string | null) {
    if (recentWorkspaceId && workspacesMetadata.some(workspace => workspace.id === recentWorkspaceId)) {
        return recentWorkspaceId;
    }

    if (workspacesMetadata.length === 1) {
        return workspacesMetadata[0].id;
    }

    const nonGuestWorkspaces = workspacesMetadata.filter(workspace => workspace.userRole !== WorkspaceUserRole.GUEST);

    const teamOrEnterpriseWorkspace = nonGuestWorkspaces.find(workspace => workspace.plan.tier === WorkspacePlanTier.TEAM || workspace.plan.tier === WorkspacePlanTier.ENTERPRISE);
    if (teamOrEnterpriseWorkspace) {
        return teamOrEnterpriseWorkspace.id;
    }
    const proWorkspace = nonGuestWorkspaces.find(workspace => workspace.plan.tier === WorkspacePlanTier.PRO);
    if (proWorkspace) {
        return proWorkspace.id;
    }

    if (nonGuestWorkspaces.length > 0) {
        return [...nonGuestWorkspaces.sort((a, b) => a.createdAt - b.createdAt)][0].id;
    }

    return [...workspacesMetadata.sort((a, b) => a.createdAt - b.createdAt)][0].id;
}

async function persistWorkspaceIdToUserRecord(workspaceId: string) {
    const existingUserModel = app.user;
    const userUid = existingUserModel?.id ?? auth().currentUser?.uid;
    if (!userUid) {
        return;
    }

    try {
        existingUserModel?.update({ recentWorkspaceId: workspaceId }, { save: false, silent: true });
        await db("users").child(userUid).child("recentWorkspaceId").set(workspaceId);
    } catch (err) {
        logger.error(err, "[WorkspaceController] persistWorkspaceIdToUserRecord() failed");
    }
}

/**
 * Sets the persistent workspace id
 *
 * Does not throw!
 *
 * @param workspaceId The id of the workspace to set
 * @param workspaceAction The action to set (optional)
 */
export function setPersistentWorkspaceId(workspaceId: string, workspaceAction?: "CREATED" | "JOINED") {
    try {
        if (workspaceAction) {
            localStorage.setItem(BEAUTIFUL_WORKSPACE_ACTION, workspaceAction);
        }

        void persistWorkspaceIdToUserRecord(workspaceId);
    } catch (err) {
        logger.error(err, "[WorkspaceController] setPersistentWorkspaceId() failed");
    }
}

/**
 * Returns plan permissions for workspace, falling back to restricted permissions if doesn't have access
 *
 * Stateless if the controller is not initialized
 *
 * @param workspaceId The id of the workspace to get permissions for
 * @returns The workspace plan permissions, or restricted permissions if doesn't have access
 */
export async function getPlanPermissionsForWorkspace(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.planPermissions;
    }

    try {
        const { permissions } = await workspacesApi.getWorkspacePlan({ workspaceId });
        return permissions;
    } catch (err) {
        // If user has no access to the workspace, return restricted permissions instead of throwing
        if (err.status === 403 || err.status === 401) {
            return GET_DEFAULT_RESTRICTED_PLAN_PERMISSIONS();
        }

        throw err;
    }
}

/**
 * Returns action permissions for workspace, falling back to restricted permissions if doesn't have access
 *
 * Stateless if the controller is not initialized
 *
 * @param workspaceId The id of the workspace to get permissions for
 * @returns The workspace action permissions, or restricted permissions if doesn't have access
 */
export async function getActionPermissionsForWorkspace(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.actionPermissions;
    }

    try {
        return await workspacesApi.getUserActionPermissions({ workspaceId });
    } catch (err) {
        if (err.status === 403 || err.status === 401) {
            return GET_DEFAULT_RESTRICTED_ACTION_PERMISSIONS();
        }

        throw err;
    }
}

/**
 * Creates a new workspace
 *
 * Stateless, but will switch workspace and reload related data if the controller is initialized
 */
export async function createWorkspace({ name, analyticsProps }: { name: string, analyticsProps?: { [key: string]: any } }) {
    const workspace = await workspacesApi.createWorkspace({ name });

    if (AppController.initialized) {
        await AppController.user.loadWorkspacesMetadata();
        await AppController.switchWorkspace(workspace.id);
    }
    if (app.user) {
        // UGLY
        await app.user.loadWorkspacesMetadata();
    }

    const createdProps = {
        workspace_id: workspace.id,
        organization_name: name, // Legacy prop, still used in Amplitude
        workspace_name: name,
        ...analyticsProps
    };
    trackActivity("Organization", "Created", null, null, createdProps, { audit: true });
    await Api.klaviyoTrack.post({
        eventName: "Organization:Created",
        createdProps,
    });

    return workspace;
}

/**
 * Deletes a workspace.
 * Allows deleting workspaces only with one user and without active subscription.
 *
 * ☠️☠️☠️ DANGER ☠️☠️☠️
 *
 * This action is not reversible, use with extreme caution
 *
 * ☠️☠️☠️ DANGER ☠️☠️☠️
 *
 * Stateless, but will throw if the controller is initialized with the same workspace
 */
export async function deleteWorkspace(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        throw new Error("Cannot delete current workspace");
    }

    await workspacesApi.deleteWorkspace({ workspaceId });
}

/**
 * Updates a workspace
 *
 * Stateless if the controller is not initialized
 */
export async function updateWorkspace(workspaceId: string, update: UpdateWorkspaceRequest["update"]) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return await workspaceController.updateWorkspace(update);
    }

    return await workspacesApi.updateWorkspace({ workspaceId, update });
}

/**
 * Returns the plan data for a workspace
 *
 * Stateless if the controller is not initialized
 */
export async function getWorkspacePlan(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.plan;
    }

    return await workspacesApi.getWorkspacePlan({ workspaceId });
}

/**
 * Invites users to a workspace
 *
 * Stateless if the controller is not initialized
 */
export async function inviteWorkspaceUsers(
    workspaceId: string,
    users: Pick<IWorkspaceUserInvite, "email" | "role">[],
    analyticsProps?: { [key: string]: any }
) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        await workspaceController.inviteWorkspaceUsers(users, analyticsProps);
        return workspaceController.userInvites;
    }

    const invites = await workspacesApi.inviteWorkspaceUsers({ workspaceId, users });

    _trackInviteWorkspaceUsers(workspaceId, users, analyticsProps);

    return invites;
}

/**
 * Returns the users for a workspace
 *
 * Stateless if the controller is not initialized
 */
export async function getWorkspaceUsers(workspaceId: string, uids?: string[], roles?: WorkspaceUserRole[]) {
    const users: IWorkspaceUserExtended[] = [];

    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        if (!uids && !roles) {
            return workspaceController.workspaceUsers;
        }

        users.push(...workspaceController.workspaceUsers.filter(user => uids?.includes(user.uid) || roles?.includes(user.role)));

        const missingUids = uids?.filter(uid => !users.some(user => user.uid === uid)) ?? [];
        const missingUsers = await workspacesApi.getWorkspaceUsers({ workspaceId, uids: missingUids, roles });
        users.push(...missingUsers);

        return users;
    }

    return await workspacesApi.getWorkspaceUsers({ workspaceId, uids, roles });
}

/**
 * Returns the user invites for a workspace
 *
 * Stateless if the controller is not initialized
 */
export async function getWorkspaceUserInvites(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.userInvites;
    }

    return await workspacesApi.getWorkspaceUserInvites({ workspaceId });
}

/**
 * Returns the workspace data
 *
 * Stateless if the controller is not initialized
 */
export async function getWorkspace(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.workspace;
    }

    return await workspacesApi.getWorkspace({ workspaceId });
}

/**
 * Returns the user groups for a workspace
 *
 * Stateless if the controller is not initialized
 */
export async function getWorkspaceUserGroups(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.userGroups;
    }

    return await workspacesApi.getWorkspaceUserGroups({ workspaceId });
}

/**
 * Returns the resources for a workspace
 *
 * Stateless if the controller is not initialized
 */
export async function getWorkspaceResources({ workspaceId, resources }: GetWorkspaceResourcesRequest) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.resources.filter(resource => resources.some(r => r.resourceId === resource.resourceId && r.resourceType === resource.resourceType));
    }

    return await workspacesApi.getWorkspaceResources({ workspaceId, resources });
}

/**
 * Returns the workspaces metadata for the current user
 *
 * Stateless
 */
export async function getWorkspacesMetadata({ ids }: { ids?: string[] } = {}) {
    return await workspacesApi.getWorkspacesMetadata({ ids });
}

/**
 * Returns **composed** user permissions to a resource, use for checking if user has access to a resource and what permissions they have
 *
 * Stateless
 */
export async function getUserPermissionsToResourceFolder({ workspaceId, folderId, uid, includeImplicitPermissions }: { workspaceId: string, folderId: string, uid?: string, includeImplicitPermissions?: boolean }) {
    return await workspacesApi.getUserPermissionsToResourceFolder({
        workspaceId,
        folderId,
        uid,
        includeImplicitPermissions
    });
}

/**
 * Returns **composed** user permissions to a resource, use for checking if user has access to a resource and what permissions they have
 *
 * Stateless
 */
export async function getUserPermissionsToResource({ workspaceId, resourceId, resourceType, uid }: { workspaceId: string, resourceId: string, resourceType: WorkspaceResourceType, uid?: string }) {
    return await workspacesApi.getUserPermissionsToResource({
        workspaceId,
        resourceId,
        resourceType,
        uid
    });
}

/**
 * Returns a list of workspace permissions to a resource
 *
 * Stateless
 */
export async function getWorkspacePermissionsToResource({ workspaceId, resourceId, resourceType }: { workspaceId: string, resourceId: string, resourceType: WorkspaceResourceType }) {
    return await workspacesApi.getWorkspacePermissionsToResource({
        workspaceId,
        resourceId,
        resourceType
    });
}

/**
 * Returns a list of collaboration invites to a resource
 *
 * Stateless
 */
export async function getCollaborationInvitesToResource({ workspaceId, resourceId, resourceType }: { workspaceId: string, resourceId: string, resourceType: WorkspaceResourceType }) {
    return await workspacesApi.getCollaborationInvitesToResource({
        workspaceId,
        resourceId,
        resourceType
    });
}

/**
 * Deletes a user from a workspace
 *
 * Stateless - does not update WorkspaceController state.
 * Use this when removing yourself from a workspace to avoid state refresh errors.
 * For removing other users, use WorkspaceController.deleteWorkspaceUser() instead.
 *
 * Stateless, but will throw if there are no members left in the workspace as it's not possible to leave the workspace if there are no members left.
 */
export async function deleteWorkspaceUser({ workspaceId, uid, transferOption, targetUid }:
    { workspaceId: string, uid: string, transferOption: "other" | "revoke" | "self", targetUid?: string }) {
    const allUsers = await getWorkspaceUsers(workspaceId);

    // This is done to avoid orphaned workspaces.
    if (allUsers.length === 1) {
        throw new ConflictError("Workspace should be deleted instead as it has no remaining members.");
    }

    // If the user is deleting themselves, we need to check if they are deleting the default workspace
    // and if so, throw an error
    if (auth().currentUser.uid === uid) {
        let defaultWorkspaceMetadata: IWorkspaceMetadata = await workspacesApi.getDefaultWorkspaceMetadata({});
        if (defaultWorkspaceMetadata.id === workspaceId) {
            throw new ConflictError("You cannot leave the default workspace.");
        }
    }

    const { deletedUser, transferredResourceCounts } = await workspacesApi.deleteWorkspaceUser({ workspaceId, uid, transferOption, targetUid });

    //// Legacy analytics ////
    const legacyRole = getLegacyRoleFromWorkspaceUserRole(deletedUser.role);
    const props = {
        workspace_id: workspaceId,
        affected_userid: uid,
        affected_user_role: AnalyticsRolesAndLicensesMap[legacyRole],
        affected_user_license: AnalyticsRolesAndLicensesMap[deletedUser.role === WorkspaceUserRole.GUEST ? "free" : "team_pro"],
        affected_user_presentations_owned: transferredResourceCounts[WorkspaceResourceType.PRESENTATION],
        presentations_transferred_to_personal: transferOption === "self" ? transferredResourceCounts[WorkspaceResourceType.PRESENTATION] : 0,
        presentations_transferred_to_team_member: transferOption === "other" ? transferredResourceCounts[WorkspaceResourceType.PRESENTATION] : 0,
        presentation_recipient_user_id: targetUid
    };
    trackActivity("Organization", "UserRemoved", null, null, props, { audit: true });
    ////

    return deletedUser;
}
